import React, { lazy } from 'react';

import { string, bool } from 'prop-types';

import { Route } from 'react-router-dom';

import { USER_TYPES } from 'constants/userTypes';
import Api from 'core/api';
import { MENTOR_VIDEOS, PROFILE_EDIT } from 'routes/constants';

import ClearCache from 'routes/PublicRoutes/ClearCache';

const WhoamiPage = lazy(
  () => import(/* webpackChunkName: 'whoami-page' */ 'modules/WhoamiPage'),
);

const PublicSurveys = lazy(
  () =>
    import(
      /* webpackChunkName: 'public-surveys' */ 'modules/WouldYouRather/Public'
    ),
);

const MobileUpload = lazy(
  () =>
    import(
      /* webpackChunkName: 'mobile-upload' */ 'modules/CollegeCosts/pages/MobileUpload'
    ),
);

const PublicCourseCatalog = lazy(
  () =>
    import(
      /* webpackChunkName: 'public-course-catalog' */ 'modules/PublicCourseCatalog/PublicPage'
    ),
);

const PublicPageRedirect = lazy(
  () =>
    import(
      /* webpackChunkName: 'magic-link' */ 'components/Common/PublicPageRedirect'
    ),
);

const PublicPath = lazy(
  () =>
    import(/* webpackChunkName: 'public-path' */ 'modules/Assessments/Public'),
);

const ScholarshipAwardMobileUpload = lazy(
  () =>
    import(
      /* webpackChunkName: 'scholarship-award-mobile-upload' */ 'modules/CollegeCosts/pages/Scholarships/MobileUpload'
    ),
);

const AcceptanceLetterMobileUpload = lazy(
  () =>
    import(
      /* webpackChunkName: 'acceptance-letter-mobile-upload' */ 'modules/CollegeApplicationManager/pages/AcceptanceLetterMobileUpload'
    ),
);

const StudentIndicatorMobileUpload = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-indicator-mobile-upload' */ 'modules/IndicatorVerification/pages/StudentIndicatorMobileUpload'
    ),
);

const VerificationUpload = lazy(
  () =>
    import(
      /* webpackChunkName: 'verification-upload' */ 'modules/RecordCenter/Public/pages/VerificationMobileUpload'
    ),
);

const RecordCenter = lazy(
  () =>
    import(
      /* webpackChunkName: 'record-center' */ 'modules/RecordCenter/Public'
    ),
);

const PublicProgramViews = lazy(
  () =>
    import(
      /* webpackChunkName: 'public-program-views' */ 'modules/IndustryPartnerProgramExploration/pages/PublicProgramViews'
    ),
);

const StudentProfile = lazy(
  () =>
    import(/* webpackChunkName: 'student-profile' */ 'modules/StudentProfile'),
);

const CredentialWallet = lazy(
  () =>
    import(
      /* webpackChunkName: 'credential-wallet' */ 'modules/CredentialWallet/routes'
    ),
);

const PublicEventSchedulerRoutes = lazy(
  () =>
    import(
      /* webpackChunkName: 'public-event-scheduler-routes' */ 'modules/EventSchedulerV2/Public'
    ),
);

const GuestUserRedirect = lazy(
  () =>
    import(
      /* webpackChunkName: 'guest-user-redirect' */ 'modules/EventSchedulerV2/Public/components/GuestUserRedirect'
    ),
);

const EventSchedulerSurveys = lazy(
  () =>
    import(
      /* webpackChunkName: 'event-scheduler-surveys' */ 'modules/EventSchedulerV2/components/EventSchedulerSurveys'
    ),
);

const IPDBIntakePage = lazy(
  () =>
    import(
      /* webpackChunkName: 'ipdb-intake-page' */ 'modules/IndustryPartnerCRM/components/IPDBIntakePage'
    ),
);

const IPDBIntakeCompletionPage = lazy(
  () =>
    import(
      /* webpackChunkName: 'ipdb-intake-completion-page' */ 'modules/IndustryPartnerCRM/components/IPDBIntakeCompletionPage'
    ),
);

const PublicRoutes = ({ type, isLoggedIn }) => (
  <>
    <Route path="/wyr/*" element={<PublicSurveys />} />
    <Route path="/path/*" element={<PublicPath />} />
    <Route
      path={`${MENTOR_VIDEOS}/:hashId`}
      element={
        <PublicPageRedirect
          apiFunc={Api.common.getMobileLink}
          apiFuncName="common.getMobileLink"
          redirectTo={MENTOR_VIDEOS}
        />
      }
    />
    <Route
      path={`${PROFILE_EDIT}/:hashId`}
      element={
        <PublicPageRedirect
          apiFunc={Api.common.getMobileLink}
          apiFuncName="common.getMobileLink"
          redirectTo={PROFILE_EDIT}
        />
      }
    />
    <Route
      path="/file-upload/:hashId"
      element={
        <PublicPageRedirect
          apiFunc={Api.common.getMobileLink}
          apiFuncName="common.getMobileLink"
          redirectTo="/file-upload"
        />
      }
    />
    <Route
      path="/course-catalog/:districtSlug/*"
      element={<PublicCourseCatalog />}
    />
    <Route path="/cc-upload/:shortCode" element={<MobileUpload />} />
    <Route
      path="/sa-upload/:shortCode"
      element={<ScholarshipAwardMobileUpload />}
    />
    <Route
      path="/al-upload/:shortCode"
      element={<AcceptanceLetterMobileUpload />}
    />
    <Route
      path="/si-upload/:shortCode"
      element={<StudentIndicatorMobileUpload />}
    />
    <Route path="/whoami" element={<WhoamiPage />} />
    <Route path="/av-upload/:shortCode" element={<VerificationUpload />} />
    {!isLoggedIn && (
      <Route path="/record-requests/*" element={<RecordCenter />} />
    )}
    <Route path="/clear-cache" element={<ClearCache />} />
    <Route
      path="/ipdb-intake/:crmOrganizationHashId/:ipdbOrganization"
      element={<IPDBIntakePage />}
    />
    <Route
      path="/ipdb-intake-completion/:ipdbOrganization"
      element={<IPDBIntakeCompletionPage />}
    />
    {type === 'guest' && (
      <>
        <Route
          path="/program-exploration/:userType/*"
          element={<PublicProgramViews />}
        />
        <Route
          path="/students/:studentId/profile"
          element={
            <StudentProfile mode="view" type="guest" guestSubject="student" />
          }
        />
        <Route
          path="/members/:memberId/profile"
          element={
            <StudentProfile mode="view" type="guest" guestSubject="member" />
          }
        />
        <Route
          path="/public-placement/*"
          element={<PublicProgramViews isBYOP />}
        />
        <Route
          path="/credential-wallet/:studentId/*"
          element={<CredentialWallet mode="public" />}
        />
      </>
    )}
    {type === USER_TYPES.ORGANIZATION_GUEST && (
      <>
        <Route path="/" element={<GuestUserRedirect />} />
        <Route path="/events/*" element={<PublicEventSchedulerRoutes />} />
        <Route
          path="/event-scheduler/survey/:surveyInstanceId/:userSurveyId/:eventId"
          element={
            <EventSchedulerSurveys
              layoutProps={{
                color: 'green',
                appBar: true,
                sideNav: false,
                appBarPageDetails: {
                  isPublicMemberPage: true,
                },
              }}
            />
          }
        />
      </>
    )}
  </>
);

PublicRoutes.propTypes = {
  type: string.isRequired,
  isLoggedIn: bool.isRequired,
};

export default PublicRoutes;
